// On-demand mid-season Progress Report (opened from the header phase chip):
// the report-card engine run at the season's current pace, plus the front
// office ledger so far. Works in any league; challenge saves carry their
// player ID + code so the share card keeps recruiting.

import { PHASE } from "../../common/constants.ts";
import { idb } from "../db/index.ts";
import { g } from "../util/index.ts";
import type { UpdateEvents } from "../../common/types.ts";
import getChallenge from "../core/league/getChallenge.ts";
import { computeProjectedReportCard } from "../core/season/projectedReportCard.ts";
import {
	computeChallengeLedger,
	type ChallengeLedger,
} from "../core/season/challengeLedger.ts";

const updateProgressReport = async (
	inputs: unknown,
	updateEvents: UpdateEvents,
) => {
	if (
		updateEvents.includes("firstRun") ||
		updateEvents.includes("gameSim") ||
		updateEvents.includes("newPhase") ||
		updateEvents.includes("playerMovement")
	) {
		const season = g.get("season");
		const tid = g.get("userTid");
		const ch = getChallenge();

		const projected = await computeProjectedReportCard(tid, season);

		const ts = await idb.cache.teamSeasons.indexGet("teamSeasonsByTidSeason", [
			tid,
			season,
		]);

		// The moves window: for a challenge, its own frame (with the starting-
		// roster snapshot); otherwise "this year's front office" — the offseason
		// tail of last season (re-sign + FA phases) through today.
		const ledgerFrame =
			ch && ch.tid === tid
				? ch
				: {
						tid,
						startSeason: season - 1,
						startPhase: PHASE.RESIGN_PLAYERS,
						endSeason: season,
					};
		let ledger: ChallengeLedger = {};
		const ledgerPlayers = await idb.getCopies.players(
			{ activeSeason: season },
			"noCopyCache",
		);
		ledger = computeChallengeLedger(ledgerPlayers, ledgerFrame);

		const t = g.get("teamInfoCache")[tid];

		// The roster driving the season: fuzzed ratings (what the user's scouts
		// believe) + per-game production, ordered by minutes so the actual
		// rotation tops the list.
		const roster = await idb.getCopies.playersPlus(
			await idb.cache.players.indexGetAll("playersByTid", tid),
			{
				attrs: ["pid", "firstName", "lastName"],
				ratings: ["ovr", "pot"],
				stats: ["gp", "min", "pts", "trb", "ast"],
				season,
				showNoStats: true,
				showRookies: true,
				fuzz: true,
				tid,
			},
		);
		const players = roster
			.sort((a, b) => (b.stats.min ?? 0) - (a.stats.min ?? 0))
			.slice(0, 8)
			.map((p) => ({
				pid: p.pid as number,
				name: `${(p.firstName as string)[0] ?? ""}. ${p.lastName}`,
				ovr: p.ratings.ovr as number,
				pot: p.ratings.pot as number,
				gp: (p.stats.gp ?? 0) as number,
				pts: (p.stats.pts ?? 0) as number,
				trb: (p.stats.trb ?? 0) as number,
				ast: (p.stats.ast ?? 0) as number,
			}));

		return {
			players,
			season,
			teamName: t ? `${t.region} ${t.name}` : "",
			abbrev: t?.abbrev ?? "",
			won: ts?.won ?? 0,
			lost: ts?.lost ?? 0,
			gp: projected?.gp ?? 0,
			paceWon: projected?.paceWon,
			projectedWins: ts?.projectedWins,
			card: projected?.card,
			ledger,
			// Challenge framing for the share card, when this save is one.
			challengeCode: ch?.code,
			challengePlayerId: ch?.playerId,
		};
	}
};

export default updateProgressReport;
